import { z } from 'zod';
import { CONDICIONES_FICHA, TIPOS_SUMINISTRO } from '../dominio.js';
import { estado, opcional, uuid } from './comunes.js';
/**
 * LOG-FT-03 Ficha de Selección y LOG-FT-04 Ficha de Evaluación del Proveedor.
 *
 * Comparten estructura —cabecera de proveedor + criterios calificados 3/2/1—
 * así que van en un solo módulo. Los distingue `tipo`, y cada uno tiene su
 * propio juego de criterios y su propia serie.
 */
export const TIPOS_FICHA = ['SELECCION_PROVEEDOR', 'EVALUACION_PROVEEDOR'];
export const ETIQUETA_FICHA = {
    SELECCION_PROVEEDOR: 'Ficha de selección',
    EVALUACION_PROVEEDOR: 'Ficha de evaluación',
};
/** Escala del formato impreso. */
export const CALIFICACIONES = [
    { valor: 3, etiqueta: 'Bueno' },
    { valor: 2, etiqueta: 'Regular' },
    { valor: 1, etiqueta: 'Malo' },
];
export const criterioFicha = z.object({
    id: z.number().int(),
    tipo: z.enum(TIPOS_FICHA),
    numero: z.number().int(),
    descripcion: z.string(),
});
/**
 * Calificación de un criterio. `null` significa «no aplica» y lo excluye del
 * promedio: la nota al pie del LOG-FT-04 lo permite expresamente.
 */
export const calificacionEntrada = z.object({
    criterioId: z.number().int().positive(),
    calificacion: z.number().int().min(1).max(3).nullable(),
    observaciones: opcional(z.string().trim().max(1000)),
});
export const fichaEntrada = z
    .object({
    tipo: z.enum(TIPOS_FICHA),
    empresaId: z.number().int().positive('Elija la empresa emisora'),
    correlativo: z.number().int().min(1, 'El correlativo debe ser mayor a cero').max(999_999),
    proveedorId: uuid,
    fechaEmision: z.iso.date(),
    tipoSuministro: z.enum(TIPOS_SUMINISTRO),
    /** Solo en evaluación: qué orden se está evaluando. */
    ordenId: opcional(uuid),
    observaciones: opcional(z.string().trim().max(2000)),
    calificaciones: z.array(calificacionEntrada).min(1, 'Califique al menos un criterio'),
})
    .refine((v) => v.calificaciones.some((c) => c.calificacion !== null), {
    path: ['calificaciones'],
    message: 'Califique al menos un criterio: sin ninguno no hay puntaje',
})
    .refine((v) => v.tipo === 'EVALUACION_PROVEEDOR' || v.ordenId == null, {
    path: ['ordenId'],
    message: 'Solo la ficha de evaluación se asocia a una orden',
});
export const fichaEdicion = z.object({
    empresaId: z.number().int().positive().optional(),
    correlativo: z.number().int().min(1).max(999_999).optional(),
    fechaEmision: z.iso.date().optional(),
    tipoSuministro: z.enum(TIPOS_SUMINISTRO).optional(),
    ordenId: opcional(uuid),
    observaciones: opcional(z.string().trim().max(2000)),
    calificaciones: z.array(calificacionEntrada).min(1).optional(),
});
export const calificacion = z.object({
    criterioId: z.number().int(),
    numero: z.number().int(),
    descripcion: z.string(),
    calificacion: z.number().int().nullable(),
    observaciones: z.string().nullable(),
});
export const fichaResumen = z.object({
    id: uuid,
    numero: z.string(),
    tipo: z.enum(TIPOS_FICHA),
    estado,
    fechaEmision: z.string(),
    empresa: z.string(),
    proveedor: z.string(),
    proveedorRuc: z.string(),
    tipoSuministro: z.enum(TIPOS_SUMINISTRO),
    /** Los calcula la base desde las calificaciones. */
    puntajePromedio: z.number().nullable(),
    condicion: z.enum(CONDICIONES_FICHA).nullable(),
    periodicidad: z.string().nullable(),
    fechaProximaEvaluacion: z.string().nullable(),
});
export const ficha = fichaResumen.extend({
    empresaId: z.number().int(),
    correlativo: z.number().int(),
    proveedorId: uuid,
    ordenId: uuid.nullable(),
    ordenNumero: z.string().nullable(),
    observaciones: z.string().nullable(),
    calificaciones: z.array(calificacion),
});
/**
 * Umbral de aprobación: `>= 2`.
 *
 * La plantilla es contradictoria —la fórmula usa `>=2` y la nota escrita dice
 * `>2`— y se optó por la fórmula, que es lo que el proceso calculó hasta hoy.
 * Ver docs/01-auditoria-plantillas.md, ambigüedad 6.
 */
export function condicionPorPuntaje(puntaje) {
    if (puntaje === null)
        return null;
    if (puntaje >= 2)
        return 'APROBADO';
    if (puntaje > 1)
        return 'APROBADO - REGULAR';
    return 'NO APROBADO';
}
export const COLOR_CONDICION = {
    APROBADO: 'teal',
    'APROBADO - REGULAR': 'yellow',
    'NO APROBADO': 'red',
};
//# sourceMappingURL=ficha.js.map